/**
 * Cart Validation Core Module
 * Handles validation of cart items against customer order history
 */

class CartValidator {
  constructor(config = {}) {
    this.config = {
      debug: config.debug || false,
      shopifyRoutes: config.shopifyRoutes || window.routes,
      ...config,
    };

    this.purchasedProducts = new Set();
    this.productsInPurchasedPacks = new Set();
    this.isInitialized = false;
  }

  /**
   * Initialize the cart validator with customer data
   */
  init(customerData) {
    if (!customerData || !customerData.isLoggedIn) {
      this.log("Customer not logged in - validation disabled");
      return false;
    }

    this.processPurchasedProducts(customerData.orders);
    this.isInitialized = true;
    this.log("Cart validator initialized with customer data");
    return true;
  }

  /**
   * Process customer orders to build purchased products sets
   */
  processPurchasedProducts(orders) {
    if (!orders || !Array.isArray(orders)) {
      this.log("No orders provided");
      return;
    }

    orders.forEach((order) => {
      order.lineItems.forEach((item) => {
        // Add directly purchased product
        this.purchasedProducts.add(item.productId.toString());

        // Process pack contents if available - removed this because we want users to pushase packs even if they own some items
        // if (item.productPack && Array.isArray(item.productPack)) {
        //   item.productPack.forEach(packItem => {
        //     this.productsInPurchasedPacks.add(packItem.id.toString());
        //   });
        // }
      });
    });

    this.log("Processed purchased products:", {
      directPurchases: Array.from(this.purchasedProducts),
      packInclusions: Array.from(this.productsInPurchasedPacks),
    });
  }

  /**
   * Validate cart and return items that should be removed
   */
  async validateCart() {
    if (!this.isInitialized) {
      this.log("Cart validator not initialized");
      return { itemsToRemove: [], error: "Not initialized" };
    }

    try {
      const cart = await this.fetchCart();

      if (!cart.items || cart.items.length === 0) {
        this.log("Cart is empty");
        return { itemsToRemove: [], cart };
      }

      const itemsToRemove = this.findItemsToRemove(cart.items);

      this.log(`Found ${itemsToRemove.length} items to remove from cart`);

      return { itemsToRemove, cart };
    } catch (error) {
      this.log("Error validating cart:", error);
      return { itemsToRemove: [], error: error.message };
    }
  }

  /**
   * Remove items from cart
   */
  async removeItems(itemsToRemove) {
    if (!itemsToRemove || itemsToRemove.length === 0) {
      return { success: true, cart: await this.fetchCart() };
    }

    try {
      const updates = {};
      itemsToRemove.forEach((item) => {
        updates[item.variantId] = 0;
      });

      const updatedCart = await this.updateCart(updates);

      this.log(`Successfully removed ${itemsToRemove.length} items from cart`);

      return { success: true, cart: updatedCart, removedItems: itemsToRemove };
    } catch (error) {
      this.log("Error removing items from cart:", error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Find cart items that should be removed
   */
  findItemsToRemove(cartItems) {
    const itemsToRemove = [];
    const seenVariants = new Map(); // Track variant IDs and their first occurrence

    cartItems.forEach((cartItem, index) => {
      const cartProductId = cartItem.product_id.toString();
      const variantId = cartItem.variant_id.toString();
      let shouldRemove = false;
      let removalReason = "";

      // Check for duplicate variants (same item added multiple times)
      if (seenVariants.has(variantId)) {
        shouldRemove = true;
        removalReason = "duplicate item in cart";
      } else {
        // Mark this variant as seen
        seenVariants.set(variantId, index);
      }

      // Check if item was directly purchased
      if (this.purchasedProducts.has(cartProductId)) {
        shouldRemove = true;
        removalReason = "already purchased";
      }

      // Check if item is part of a purchased pack
      if (this.productsInPurchasedPacks.has(cartProductId)) {
        shouldRemove = true;
        removalReason = "part of purchased pack";
      }

      if (shouldRemove) {
        itemsToRemove.push({
          line: index + 1,
          title: cartItem.title,
          variantId: cartItem.variant_id,
          productId: cartProductId,
          reason: removalReason,
        });

        this.log(`Item to remove: ${cartItem.title} (${removalReason})`);
      }
    });

    return itemsToRemove;
  }

  /**
   * Fetch current cart from Shopify
   */
  async fetchCart() {
    const response = await fetch(`${this.config.shopifyRoutes.root || "/"}cart.js`);
    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.status}`);
    }
    return response.json();
  }

  /**
   * Update cart using Shopify API
   */
  async updateCart(updates) {
    const response = await fetch(`${this.config.shopifyRoutes.root || "/"}cart/update.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ updates }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update cart: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Debug logging
   */
  log(message, data = null) {
    if (this.config.debug) {
      console.log(`[CartValidator] ${message}`, data || "");
    }
  }
}

// Export for use in other modules
window.CartValidator = CartValidator;
